import { QiraMessageContentChunk } from 'api3';
import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, computed, inject } from '@angular/core';
import { ChildRendererService } from './child-renderer.service';
import { DynamicXMLElementComponent } from './dynamic.component';
import { Chapter, VWData, VWRawData } from './volkswagen.types';
import { TranslocoPipe } from '@jsverse/transloco';
import * as Sentry from '@sentry/angular';

@Component({
  selector: 'app-vw-renderer',
  templateUrl: './vw-renderer.component.html',
  imports: [CommonModule, DynamicXMLElementComponent, TranslocoPipe],
  standalone: true,
})
export class VWRendererComponent implements OnDestroy {
  @Input() rawData!: object | undefined;
  @Input() content?: QiraMessageContentChunk;
  @Input() provider!: string;
  @Input() tecDocPrototypes?: string[];

  dbId = '';

  childRenderService = inject(ChildRendererService);

  trackByIndex(index: number, item: any): any {
    void item;
    return index;
  }

  trackByChapter(index: number, chapter: Chapter): string {
    return chapter.id;
  }

  printableList(text: string | undefined): string | undefined {
    if (!text) return undefined;

    return text
      .split(',')
      .sort()
      .map((item) => item.trim())
      .join(', ');
  }

  getFilteredChildren(children: any[] | undefined): any[] {
    if (!children) return [];

    if (!this.tecDocPrototypes || this.tecDocPrototypes.length === 0) return children;

    const prototypePrefixes = [...new Set(this.tecDocPrototypes.map((prototype) => prototype.substring(0, 2).toLowerCase()))];

    const filtered = children.filter((child) => {
      const verkaufstyp = (child?.verkaufstyp || '').toLowerCase();
      return prototypePrefixes.some((prefix) => verkaufstyp.includes(prefix));
    });

    return filtered.length > 0 ? filtered : children;
  }

  vwData = computed<VWData | undefined>(() => {
    try {
      const data = this.rawData ? this.rawData : this.content?.document?.data;
      const parsedData = typeof data === 'string' ? (JSON.parse(data as unknown as string) as VWRawData['xml_data']) : data;

      if (!parsedData) {
        return undefined;
      }

      if ('tag' in parsedData && parsedData.tag === 'slp-block') {
        this.dbId = parsedData?.['db-id'];

        return {
          title: parsedData.children.find((c: any) => c.tag === 'titel')?.text || 'Wiring Diagram',
          dbId: parsedData['db-id'],
          xml_data: {
            tag: parsedData.tag,
            children: parsedData.children,
          },
          chapters: [],
        } as VWData;
      } else {
        this.dbId = parsedData?.xml_data?.dbId;

        return {
          title: parsedData.title,
          dbId: parsedData.dbId,
          xml_data: parsedData.xml_data,
          chapters: [parsedData.xml_data],
        } as VWData;
      }
    } catch (error) {
      console.error('[VWRendererComponent] Error parsing VW data:', error);
      Sentry.captureException(error, {
        tags: {
          location: 'VWRendererComponent',
          feature: 'ArticleView',
          message: "Couldn't parse VW article data.",
        },
      });
      return undefined;
    }
  });

  hasImageWithLegend = computed<boolean>(() => {
    if (this.vwData()?.xml_data?.tag === 'slp-block') return true;

    const childrenContainImageAndLegend = (element: any): boolean => {
      if (!element || !element.children || element.children.length === 0) {
        return false;
      }

      const hasImage = element.children.some((child: any) => child.tag === 'slp-bild');
      const hasLegend = element.children.some((child: any) => child.tag === 'leg-slp');
      if (hasImage && hasLegend) {
        return true;
      }

      return element.children.some((child: any) => childrenContainImageAndLegend(child));
    };

    return childrenContainImageAndLegend(this.vwData()?.xml_data);
  });

  ngOnDestroy(): void {
    this.childRenderService.cleanup();
  }
}
